import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Redirect, useRouter } from 'expo-router';
import { FlatList, RefreshControl, ScrollView, useColorScheme } from 'react-native';
import { Paragraph, XStack, YStack } from 'tamagui';

import { Text } from '@/components/text';
import { TextLink } from '@/components/text-link';
import { SolidSurface, useGlassAccentColor } from '@/components/glass-surface';
import { Heading } from '@/components/heading';
import { PrimaryButton } from '@/components/primary-button';
import { ScreenContainer } from '@/components/screen-container';
import { Skeleton } from '@/components/skeleton';
import { Wordmark } from '@/components/wordmark';
import { WebRefreshButton } from '@/components/web-refresh';
import { useAuth } from '@/hooks/auth-context';
import { usePrefersReducedMotion } from '@/hooks/use-reduced-motion';
import { api } from '@/lib/api';
import { pullToRefreshHaptic } from '@/lib/haptics';
import { queryKeys } from '@/lib/query-keys';
import { relativeTime } from '@/lib/relative-time';
import { borderWidth, darkColors, lightColors, space } from '@/theme/tokens.generated';

// Landing/index.html's own "How it works" copy (its <h2>/<p> pairs,
// verbatim) -- reused here rather than invented fresh so a visitor who
// lands directly on this screen (no auth, reachable from a shared link
// or a bookmark) sees the SAME product explanation the landing page
// already gives, not a second, slightly different pitch. Doubles as
// this screen's "empty state owns the viewport" content: three real
// paragraphs of substance instead of one centered sentence floating in
// ~60% of dead space (whole-app critique 2026-09-02).
const HOW_IT_WORKS = [
  {
    index: '01',
    title: 'Find your people, nearby.',
    body: 'Browse and join conversations happening near you: traffic, local events, recommendations, a pub or coffee shop hangout, or anything else worth talking about.',
  },
  {
    index: '02',
    title: 'Talk first.',
    body: 'No photo, no judgment, no pressure. Just a conversation, the way it actually starts with someone in real life.',
  },
  {
    index: '03',
    title: 'Reveal when you’re both ready.',
    body: 'When there’s something there, for both of you, you can choose to share who you really are. Not before. Not one-sided.',
  },
] as const;

// Issue #147 AC3: the unauthenticated preview thread list's first-load
// state used a bare, centered ActivityIndicator -- this app's very
// first impression of what threads look like, previously the least
// polished loading state in the app. Same 16px/12px rhythm as
// threads/index.tsx's own real FlatList so the swap-in doesn't jump.
function PreviewListSkeleton() {
  return (
    <YStack gap="$3" pt="$4">
      <Skeleton height={92} />
      <Skeleton height={92} />
      <Skeleton height={92} />
    </YStack>
  );
}

export function Preview() {
  const router = useRouter();
  const auth = useAuth();
  // Native semantic color, not a Tamagui token -- same reasoning as
  // threads/index.tsx's identical RefreshControl setup.
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;
  const accentColor = useGlassAccentColor();
  // #147 AC4/use-reduced-motion.ts's own gap: gating the transition
  // itself, not just hoping the driver handles it, is what actually
  // closes that gap here (that hook's own doc comment: a raw CSS
  // `transition` never automatically respects `prefers-reduced-motion`
  // on web). `mounted` drives a real prop-value CHANGE (opacity/y go
  // from their initial value to their resting one after mount) that
  // Tamagui's own `transition` prop animates -- `enterStyle`/`animation`
  // aren't part of this project's own YStack prop surface (see
  // tamagui.config.ts's own #191-incident history: an unsupported
  // animation prop crashed production once already, so this project
  // deliberately narrowed what's exposed), but `transition` (the same
  // prop Card.tsx/PrimaryButton already use safely) works the same way
  // driven by a real state change instead.
  const prefersReducedMotion = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  // GET /threads/preview is a real, registered, public route
  // (PreviewThreadsHandler, wired in backend/internal/http/router.go) --
  // an earlier version of this comment described it as not yet built,
  // which was already stale by the time Task 8 built on top of it (final
  // review, PR #51). retry: false because this is the very first thing an
  // unauthenticated visitor's session does; a real failure should surface
  // promptly with a manual retry (see the isError branch below), not
  // retry silently for several seconds first.
  const preview = useQuery({ queryKey: queryKeys.previewThreads(), queryFn: api.previewThreads, retry: false });

  // needs-signup means Supabase Auth already succeeded (OAuth or
  // email/password) and the only thing left is the age-attestation
  // step on the sign-up screen -- land there directly rather than
  // showing this screen's "Sign up" CTA to someone who's already
  // mid-signup. Checked after the hooks above (rules-of-hooks: every
  // hook must run on every render, so an early return can't come first).
  if (auth.status === 'needs-signup') return <Redirect href="/sign-up" />;

  // hasRealThreads uses preview.data directly (not preview.isSuccess,
  // which flips false on a background-refetch failure even though
  // TanStack Query leaves the previous data in place) -- a failed
  // pull-to-refresh must not hide already-loaded thread cards.
  const hasRealThreads = !!preview.data && preview.data.length > 0;

  // Real preview data fills the viewport on its own; anything else
  // (loading aside, which is transient) needs real content here instead
  // of nothing, so the screen doesn't end in ~60% dead space under a
  // single centered sentence. showsFallback covers both a genuine
  // zero-threads response and a real load failure -- both get the same
  // "How it works" filler content below, but NOT the same demoted note:
  // see the preview.isError branch there, which must stay honest about
  // an actual failure rather than implying the feature doesn't exist yet
  // (composition rule: "feedback may not outrun the truth" -- final
  // review, PR #51). Written as !hasRealThreads (exclusive with the
  // FlatList branch below) rather than the original `isError ||
  // length === 0` -- that OR could be true at the SAME time as
  // `preview.data.length > 0` once a background refetch failed with
  // real cached data present, rendering the fallback ScrollView AND
  // the real FlatList stacked in the same flex:1 area at once (caught
  // in review of the pull-to-refresh PR).
  const showsFallback = !preview.isPending && !hasRealThreads;

  return (
    <ScreenContainer>
      <YStack flex={1}>
        {/* The page's lead -- the product proposition itself ("what
            Nearkin is"), not the coming-soon caveat below it. Fraunces
            36px against this screen's ~15-16px body text is a >=2.3x
            step, matching this project's own "display type is a
            different size, not a bolder body" rule (only text on this
            screen large enough to read as a headline, not a bolded
            paragraph). Reuses landing/index.html's own "Find your
            people, nearby." H2 rather than inventing new copy -- same
            product claim in the same words, whether someone lands here
            first or on the marketing site first. */}
        <YStack pt="$2" pb="$4" gap="$3">
          {/* Refresh moved inline next to the wordmark (issue #290) --
              it used to be its own standalone row below the paragraph. */}
          <XStack alignItems="center" justifyContent="space-between">
            <Wordmark />
            <WebRefreshButton onRefresh={() => preview.refetch()} refreshing={preview.isRefetching} />
          </XStack>
          <Heading fontSize="$display" lineHeight="$display" letterSpacing={-0.5}>
            Find your people, nearby.
          </Heading>
          <Paragraph color="$colorSecondary" size="$body">
            Browse threads people are posting near you right now — no photo, no pressure, just a conversation.
          </Paragraph>
        </YStack>

        {preview.isPending && <PreviewListSkeleton />}

        {showsFallback && (
          // ScrollView, not a fixed YStack -- ScreenContainer's column
          // has no scroll of its own (ScreenContainer.tsx), and this
          // fallback content (three real paragraphs, not one short
          // sentence) can run taller than the viewport on a short
          // phone. Every other scrollable region in this app is a
          // FlatList (threads, thread-detail, this same screen's own
          // data branch below) -- this is the one non-list scrollable
          // block, so it needs its own ScrollView rather than
          // inheriting one.
          <ScrollView
            style={{ flex: 1 }}
            // paddingTop added -- issue #205/#212's own investigation:
            // with none set, scrolled content rendered flush against
            // the fixed header above with zero breathing room, reading
            // as visual overlap the moment the scroll position moved
            // even slightly (confirmed via a real headless-Chrome
            // render at a 390x550 viewport -- scrolling revealed
            // HOW_IT_WORKS body text crowding directly under the
            // header's own intro paragraph, matching this app's own
            // reported live bug).
            contentContainerStyle={{ paddingTop: space[4], paddingBottom: space[4], gap: space[5] }}
            // Same pull-to-refresh coverage as the FlatList branch below
            // -- this fallback (zero threads, or a failed first load)
            // is exactly the state a visitor is most likely to try
            // pulling to refresh from, and a ScrollView accepts the
            // same refreshControl prop a FlatList does.
            refreshControl={
              <RefreshControl
                refreshing={preview.isRefetching}
                onRefresh={() => {
                  pullToRefreshHaptic();
                  preview.refetch();
                }}
                tintColor={colors.actionPrimary}
                colors={[colors.actionPrimary]}
                progressBackgroundColor={colors.surfacePage}
              />
            }
          >
            {/* This screen's job is to persuade someone who hasn't
                signed up yet (impeccable's own "Persuade" mode -- design
                IS the product here, unlike the authenticated Threads
                list, which stays a plain scanning surface on purpose).
                The three steps used to be a small mono "01/02/03" over
                plain black/gray text -- exactly the generic "SaaS
                landing-page checklist" template (founder, 2026-09-16:
                "why does this app mostly feel like it's from the early
                2010s" -- this section was one of the concrete reasons
                named). Each step number is now a real solid badge, and the
                whole block gets one authored entrance -- a single fade
                + rise, not per-item staggering (craft-floor's own
                motion rule: "one authored moment, not scattered
                effects") -- gated on prefersReducedMotion so a reduced-
                motion visitor just sees the finished state immediately. */}
            <YStack
              gap="$4"
              transition={prefersReducedMotion ? undefined : 'medium'}
              opacity={prefersReducedMotion || mounted ? 1 : 0}
              y={prefersReducedMotion || mounted ? 0 : 16}
            >
              {HOW_IT_WORKS.map((step) => (
                <XStack key={step.index} gap="$3" alignItems="flex-start">
                  <SolidSurface
                    borderRadius="$full"
                    style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Text
                      fontFamily="$mono"
                      fontWeight="$semibold"
                      color={accentColor}
                      fontSize="$meta"
                      lineHeight="$meta"
                    >
                      {step.index}
                    </Text>
                  </SolidSurface>
                  <YStack flex={1} gap="$1" pt="$1">
                    <Text
                      fontFamily="$heading"
                      fontWeight="$semibold"
                      color="$color"
                      fontSize="$titleS"
                      lineHeight="$titleS"
                    >
                      {step.title}
                    </Text>
                    <Paragraph color="$colorSecondary">{step.body}</Paragraph>
                  </YStack>
                </XStack>
              ))}
            </YStack>

            {/* Demoted to a small secondary note under the real product
                content above it -- it was previously the visually
                largest element on the page (an H3), which is exactly
                backwards for a screen whose job is to sell the product
                to someone who hasn't signed up yet. preview.isError is a
                REAL failure (the route is live -- see the useQuery doc
                comment above), not "not built yet", so it gets an honest
                message and a retry action rather than the zero-threads
                copy, which would otherwise tell a visitor the feature
                doesn't exist during an outage it does. */}
            {preview.isError ? (
              <YStack gap="$2" alignItems="flex-start">
                <Paragraph color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
                  Couldn&apos;t load the preview. Check your connection and try again.
                </Paragraph>
                <PrimaryButton size="sm" onPress={() => preview.refetch()}>
                  Try again
                </PrimaryButton>
              </YStack>
            ) : (
              <Paragraph color="$colorTertiary" fontSize="$meta" lineHeight="$meta">
                No threads near you yet — be the first to start a conversation in your neighborhood.
              </Paragraph>
            )}
          </ScrollView>
        )}

        {hasRealThreads && (
          <>
            {/* Background refetch failed but real threads are still on
                screen -- says so rather than silently keeping stale
                content with no signal (same reasoning as threads/
                index.tsx and thread-detail/index.tsx's identical
                notices). */}
            {preview.isError && (
              <YStack pb="$2">
                <Text color="$destructive" fontSize="$meta" lineHeight="$meta" textAlign="center">
                  Couldn&apos;t refresh. Showing what&apos;s already loaded.
                </Text>
              </YStack>
            )}
            <FlatList
              style={{ flex: 1 }}
              ListFooterComponent={preview.data.length < 5 ? <HowItWorksList accentColor={accentColor} /> : null}
              data={preview.data}
              keyExtractor={(thread) => thread.id}
              contentContainerStyle={{ gap: space[3], paddingBottom: space[6] }}
              renderItem={({ item }) => (
                <SolidSurface style={{ padding: space[4] }}>
                  <YStack gap="$1">
                    <Text fontWeight="$semibold" color="$color" fontSize="$titleS" lineHeight="$titleS">
                      {item.title}
                    </Text>
                    <Text color="$colorSecondary" numberOfLines={2}>
                      {item.body}
                    </Text>
                    {/* Issue #151 AC2: this card previously showed no
                      author/time/distance context at all -- "the
                      storefront needs the most trust, not the least."
                      Author handle and distance are NOT addable here
                      without a real privacy-boundary decision: GET
                      /threads/preview is unauthenticated, unpaginated,
                      and rate-limitless, so PreviewThread deliberately
                      carries no creatorId/lat/lng (see its own doc
                      comment in lib/api.ts, Geo-privacy section) --
                      exposing either would leak identity/location to
                      any anonymous caller. createdAt DOES already exist
                      on the response, so relative time is addable with
                      zero backend change and is the one piece of real
                      context this screen can honestly show. */}
                    <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta" pt="$1">
                      {relativeTime(item.createdAt)}
                    </Text>
                  </YStack>
                </SolidSurface>
              )}
              // isRefetching, not isFetching -- isFetching is also true
              // during the very first load, which preview.isPending above
              // already covers with its own full-screen spinner.
              refreshControl={
                <RefreshControl
                  refreshing={preview.isRefetching}
                  onRefresh={() => {
                    pullToRefreshHaptic();
                    preview.refetch();
                  }}
                  tintColor={colors.actionPrimary}
                  colors={[colors.actionPrimary]}
                  progressBackgroundColor={colors.surfacePage}
                />
              }
            />
          </>
        )}
      </YStack>

      {/* Ends the page on purpose (this project's own composition
          rule) -- a second line under the CTA rather than the button
          floating alone at the bottom edge. Was "Free to browse.
          Verified real people only" -- a population claim that stopped
          being true the moment Tier 0 became opt-in rather than a
          signup gate (boardroom decision, 2026-09-05: ~/.claude/
          boardroom/2026-09-05-tier0-verification-gating.md). The
          replacement makes the actual, still-true safety claim instead
          -- no one's real photo/identity is visible until both sides
          choose to reveal, which doesn't depend on verification status
          at all. */}
      <YStack py="$4" gap="$2" borderTopWidth={borderWidth.hairline} borderTopColor="$borderColor">
        <PrimaryButton onPress={() => router.push('/sign-up')}>Sign up</PrimaryButton>
        <TextLink textAlign="center" onPress={() => router.push({ pathname: '/sign-up', params: { mode: 'sign-in' } })}>
          Already have an account? Log in
        </TextLink>
        {/* fontFamily="$body" explicit -- founder feedback, 2026-09-15:
            this read as inconsistent with the button's own label above
            it. Confirmed via a real render, not assumed: bare `<Text>`
            (imported from 'tamagui') has NO font-family default of its
            own -- it silently falls back to the browser's system-font
            stack unless a family is set explicitly, unlike Paragraph/
            Heading (built on SizableText, which DOES default to
            "$body"). This is a real, app-wide gap (grepped: dozens of
            other bare `<Text>` call sites likely have the same issue),
            tracked separately for a dedicated pass rather than papered
            over broadly here -- a global CSS-inheritance fix was tried
            and reverted: react-native-web/Tamagui's own atomic CSS
            class rules apply DIRECTLY to each text element, which
            always wins over an inherited value regardless of selector
            specificity, so fixing this safely needs either an
            !important override (risks also overriding Heading/mono's
            own intentionally-different fonts) or a real per-call-site
            audit -- out of scope to improvise safely in this pass. */}
        <Text color="$colorTertiary" fontSize="$meta" lineHeight="$meta" fontFamily="$body" textAlign="center">
          Free to browse. No photos until you&apos;re both ready.
        </Text>
      </YStack>
    </ScreenContainer>
  );
}

// Shown under a sparse thread list so a quiet neighborhood does not end in empty space.
function HowItWorksList({ accentColor }: { accentColor: string }) {
  return (
    <YStack gap="$6" pt="$6">
      {HOW_IT_WORKS.map((step) => (
        <XStack key={step.index} gap="$3" alignItems="flex-start">
          <SolidSurface
            borderRadius="$full"
            style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}
          >
            <Text fontFamily="$mono" fontWeight="$semibold" color={accentColor} fontSize="$meta" lineHeight="$meta">
              {step.index}
            </Text>
          </SolidSurface>
          <YStack flex={1} gap="$1" pt="$1">
            <Text fontFamily="$heading" fontWeight="$semibold" color="$color" fontSize="$titleS" lineHeight="$titleS">
              {step.title}
            </Text>
            <Paragraph color="$colorSecondary">{step.body}</Paragraph>
          </YStack>
        </XStack>
      ))}
    </YStack>
  );
}
